// The bundle signing key (spec section 6.1, decision 23): a P-256 ECDSA pair
// made at enrolment. The private half is wrapped under MK (AES-GCM, pkcs8) into
// `{ iv, wrapped }` and stored in the bundle beside the public half; nothing is
// exported in the clear, and the only extractable handle lives inside
// `createWrappedSigningKey` for the length of one wrap. Any device that holds
// MK can unwrap it as a non-extractable signing key and prove possession over
// the canonical message; a device without MK, a session thief and a database
// reader cannot. "Under MK" is a key derived from MK for this one purpose, so
// the raw bytes are never used as a cipher key directly, and the wrapping is
// bound to the member and the MK generation.
import { canonicalMessage, hashCanonical } from './canonical';
import { base64ToBytes, bytesToBase64, utf8Encode } from './encoding';
import { assertKeyId, assertMemberId } from './identifiers';
import { assertMasterKey } from './master-key';
import { getSubtle, hkdfKey, randomBytes } from './webcrypto';

const IV_BYTES = 12;
const ECDSA_P256 = Object.freeze({ name: 'ECDSA', namedCurve: 'P-256' });
const ECDSA_SHA256 = Object.freeze({ name: 'ECDSA', hash: 'SHA-256' });
const KEK_INFO = 'bundle-key';
const WRAP_AAD_LABEL = 'hogc-one/courses/v1/bundle-key';

export const BUNDLE_KEY_ERRORS = Object.freeze({
  AUTH_FAILED: 'AUTH_FAILED',
  MALFORMED: 'MALFORMED',
});

const ERROR_MESSAGES = Object.freeze({
  AUTH_FAILED: 'The signing key could not be unwrapped with this master key',
  MALFORMED: 'The signing key material is not valid',
});

export class BundleKeyError extends Error {
  /**
   * @param {keyof typeof BUNDLE_KEY_ERRORS} code
   * @param {string} [detail]
   */
  constructor(code, detail) {
    super(detail ? `${ERROR_MESSAGES[code]}: ${detail}` : ERROR_MESSAGES[code]);
    this.name = 'BundleKeyError';
    this.code = code;
  }
}

/**
 * @param {string} text
 * @param {string} what
 * @returns {Uint8Array}
 */
function decodeBase64(text, what) {
  try {
    return base64ToBytes(text);
  } catch (error) {
    throw new BundleKeyError(BUNDLE_KEY_ERRORS.MALFORMED, `${what} is not base64`);
  }
}

/**
 * KEK_bundle: HKDF-SHA256(MK, salt = memberId, info = "bundle-key"), limited to
 * wrapping so the handle cannot open an envelope.
 *
 * @param {Uint8Array} mk
 * @param {number} memberId
 * @returns {Promise<CryptoKey>}
 */
function deriveBundleKek(mk, memberId) {
  assertMasterKey(mk);
  assertMemberId(memberId);
  return hkdfKey({
    ikm: mk,
    salt: utf8Encode(String(memberId)),
    info: KEK_INFO,
    algorithm: { name: 'AES-GCM', length: 256 },
    usages: ['wrapKey', 'unwrapKey'],
  });
}

/**
 * @param {number} memberId
 * @param {string} keyId
 * @returns {Uint8Array}
 */
function wrapAad(memberId, keyId) {
  return utf8Encode(`${WRAP_AAD_LABEL}:${memberId}:${keyId}`);
}

/**
 * Enrolment: generate the pair, wrap the private half under MK and hand back
 * only what the bundle stores. The extractable handle never leaves this
 * function.
 *
 * @param {object} args
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId MK's generation
 * @param {number} args.memberId
 * @returns {Promise<{ publicKey: string, wrapping: { iv: string, wrapped: string } }>}
 *   `publicKey` is base64 SPKI (the bundle's `signingKey`), `wrapping` its
 *   `signingKeyWrapped`
 */
export async function createWrappedSigningKey({ mk, keyId, memberId }) {
  assertKeyId(keyId);
  const subtle = getSubtle();
  const kek = await deriveBundleKek(mk, memberId);
  const pair = await subtle.generateKey(ECDSA_P256, true, ['sign', 'verify']);
  const spki = new Uint8Array(await subtle.exportKey('spki', pair.publicKey));
  const iv = randomBytes(IV_BYTES);
  const wrapped = await subtle.wrapKey('pkcs8', pair.privateKey, kek, {
    name: 'AES-GCM',
    iv,
    additionalData: wrapAad(memberId, keyId),
  });
  return {
    publicKey: bytesToBase64(spki),
    wrapping: { iv: bytesToBase64(iv), wrapped: bytesToBase64(new Uint8Array(wrapped)) },
  };
}

/**
 * @param {string} publicKey base64 SPKI
 * @returns {Promise<CryptoKey>}
 */
export async function importSigningPublicKey(publicKey) {
  const spki = decodeBase64(publicKey, 'signing key');
  try {
    return await getSubtle().importKey('spki', spki, ECDSA_P256, true, ['verify']);
  } catch (error) {
    throw new BundleKeyError(BUNDLE_KEY_ERRORS.MALFORMED, 'signing key is not P-256 SPKI');
  }
}

/**
 * @param {unknown} wrapping
 * @returns {{ iv: Uint8Array, wrapped: Uint8Array }}
 */
function decodeWrapping(wrapping) {
  if (wrapping === null || typeof wrapping !== 'object') {
    throw new BundleKeyError(BUNDLE_KEY_ERRORS.MALFORMED, 'wrapping is not an object');
  }
  const iv = decodeBase64(wrapping.iv, 'iv');
  const wrapped = decodeBase64(wrapping.wrapped, 'wrapped');
  if (iv.length !== IV_BYTES) throw new BundleKeyError(BUNDLE_KEY_ERRORS.MALFORMED, 'iv length');
  return { iv, wrapped };
}

/**
 * @param {object} args
 * @param {unknown} args.wrapping `signingKeyWrapped` from the status response
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId the bundle's `keyId` from the same response
 * @param {number} args.memberId
 * @returns {Promise<CryptoKey>} a non-extractable key that can only sign
 */
export async function unwrapSigningKey({ wrapping, mk, keyId, memberId }) {
  assertKeyId(keyId);
  const { iv, wrapped } = decodeWrapping(wrapping);
  const kek = await deriveBundleKek(mk, memberId);
  try {
    return await getSubtle().unwrapKey(
      'pkcs8',
      wrapped,
      kek,
      { name: 'AES-GCM', iv, additionalData: wrapAad(memberId, keyId) },
      ECDSA_P256,
      false,
      ['sign'],
    );
  } catch (error) {
    throw new BundleKeyError(BUNDLE_KEY_ERRORS.AUTH_FAILED);
  }
}

/**
 * Signs one operation over the canonical message (API contract section 5).
 *
 * @param {object} args
 * @param {CryptoKey} args.signingKey from unwrapSigningKey
 * @param {'register' | 'approve' | 'revoke' | 'recovery' | 'reset' | 'cancel'} args.op
 * @param {number} args.memberId
 * @param {string} args.nonce from POST /api/me/encryption/challenge
 * @param {Record<string, string | number | boolean | null>} [args.fields]
 * @returns {Promise<{ message: string, signature: string }>} `signature` is the
 *   64-byte raw `r || s` in standard base64, the request's `signature` field
 */
export async function signOperation({ signingKey, op, memberId, nonce, fields = {} }) {
  const message = canonicalMessage(op, memberId, nonce, fields);
  const signature = await getSubtle().sign(ECDSA_SHA256, signingKey, utf8Encode(message));
  return { message, signature: bytesToBase64(new Uint8Array(signature)) };
}

/**
 * What the API does with a signed message; here so specs and self-checks can
 * prove a signature before sending it.
 *
 * @param {object} args
 * @param {string} args.publicKey base64 SPKI
 * @param {string} args.message
 * @param {string} args.signature base64
 * @returns {Promise<boolean>}
 */
/**
 * The `pendingSigningKeyWrappedHash` a `rotate-begin` signature carries (API
 * contract section 5, api#1134).
 *
 * Destructured rather than spread: the server hashes its DTO's declared
 * fields, so a caller handing this an object with anything extra on it must
 * not change the hash. A mismatch is a 403 that names no field.
 *
 * @param {{ iv: string, wrapped: string }} wrapping
 * @returns {Promise<string>}
 */
export function signingKeyWrappedHash({ iv, wrapped }) {
  return hashCanonical({ iv, wrapped });
}

export async function verifyOperation({ publicKey, message, signature }) {
  const key = await importSigningPublicKey(publicKey);
  const bytes = decodeBase64(signature, 'signature');
  return getSubtle().verify(ECDSA_SHA256, key, bytes, utf8Encode(message));
}
